const GROUPING_SELECTOR = '[data-product-media-grouping]';
const MEDIA_SELECTOR = '[data-media-id]';
const CONTROL_SELECTOR = 'button[ref="dots[]"], button[ref="thumbnails[]"]';
const productRequests = new Map();

const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const altGroups = (alt) =>
  (String(alt || '').toLowerCase().match(/#[a-z0-9_-]+/g) || [])
    .map((tag) => normalize(tag.slice(1)))
    .filter(Boolean);

class ProductMediaGrouping {
  constructor(marker) {
    this.marker = marker;
    this.root = marker.closest('.product-information') || document;
    this.productUrl = marker.dataset.productUrl;
    this.optionNames = (marker.dataset.optionNames || 'Color,Colour,Colors,Colours,Finish')
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean);
    this.groupIds = new Map();
    this.activeGroup = '';
    this.boundGallery = null;
    this.labeledGallery = null;
  }

  async connect() {
    if (!this.productUrl) return;
    if (!productRequests.has(this.productUrl)) {
      productRequests.set(this.productUrl, fetch(`${this.productUrl.split('?')[0]}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => { if (!response.ok) throw new Error(`Product media request failed: ${response.status}`); return response.json(); })
        .catch((error) => { productRequests.delete(this.productUrl); throw error; }));
    }
    const product = await productRequests.get(this.productUrl);
    const colorOption = product.options?.find((option) => this.optionNames.includes(option.name.toLowerCase()) || option.name.toLowerCase() === 'finish');
    if (!colorOption) return;
    this.colorPosition = colorOption.position;
    this.defaultGroup = product.variants?.[0]?.options?.[this.colorPosition - 1] || colorOption.values[0];

    colorOption.values.forEach((value) => {
      const key = normalize(value);
      const ids = product.media
        .filter((media) => altGroups(media.alt).includes(key))
        .map((media) => String(media.id));
      if (ids.length) this.groupIds.set(value, ids);
    });
    if (!this.groupIds.size) return;

    this.root.addEventListener('change', this.handleVariantChange, true);
    this.root.addEventListener('variant:update', this.handleVariantUpdate);
    this.apply();
  }

  refreshElements() {
    this.gallery = this.root.querySelector('media-gallery');
    this.variantPicker = this.root.querySelector('variant-picker');
    this.slideshow = this.gallery?.querySelector('slideshow-component');
    if (!this.gallery || !this.variantPicker || !this.slideshow) return false;

    if (this.boundGallery !== this.gallery) {
      this.boundGallery?.removeEventListener('click', this.handleControlClick, true);
      this.boundGallery?.removeEventListener('slideshow:select', this.handleSlideSelect);
      this.gallery.addEventListener('click', this.handleControlClick, true);
      this.gallery.addEventListener('slideshow:select', this.handleSlideSelect);
      this.boundGallery = this.gallery;
    }
    return true;
  }

  slides() {
    return Array.from(this.gallery.querySelectorAll('slideshow-slide[ref="slides[]"]'));
  }

  controls() {
    return Array.from(this.gallery.querySelectorAll(CONTROL_SELECTOR));
  }

  selectedGroup() {
    const group = this.variantPicker.querySelector(`[data-option-position="${this.colorPosition}"]`);
    const value = group?.querySelector('input:checked, select')?.value;
    return this.groupIds.has(value) ? value : this.defaultGroup;
  }

  groupForMediaId(mediaId) {
    return [...this.groupIds].filter(([, ids]) => ids.includes(String(mediaId))).map(([group]) => normalize(group)).join(',') || 'shared';
  }

  mediaIdForSlide(slide) {
    return slide?.querySelector(MEDIA_SELECTOR)?.dataset.mediaId || '';
  }

  labelElements() {
    if (this.labeledGallery === this.gallery) return;
    this.labeledGallery = this.gallery;
    const slides = this.slides();
    slides.forEach((slide) => {
      const mediaId = this.mediaIdForSlide(slide);
      slide.dataset.mediaId = mediaId;
      slide.dataset.mediaGroup = this.groupForMediaId(mediaId);
    });

    this.gallery.querySelectorAll('ul[ref="media[]"], li[ref="media[]"], .media-gallery__grid > li').forEach((item) => {
      const mediaId = item.querySelector(MEDIA_SELECTOR)?.dataset.mediaId;
      if (mediaId) item.dataset.mediaGroup = this.groupForMediaId(mediaId);
    });

    this.gallery.querySelectorAll('slideshow-controls').forEach((container) => {
      const controls = Array.from(container.querySelectorAll(CONTROL_SELECTOR));
      controls.forEach((control, index) => {
        const slide = slides[index];
        if (!slide) return;
        control.dataset.mediaId = slide.dataset.mediaId;
        control.dataset.mediaGroup = slide.dataset.mediaGroup;
      });
    });

    const zoomItems = this.gallery.querySelectorAll('.dialog-zoomed-gallery > li');
    zoomItems.forEach((item) => {
      const mediaId = item.querySelector(MEDIA_SELECTOR)?.dataset.mediaId || item.id.match(/product-(\d+)-/)?.[1];
      if (mediaId) item.dataset.mediaGroup = this.groupForMediaId(mediaId);
    });
  }

  syncSelectedControls(slide) {
    const selectedMediaId = slide?.dataset.mediaId || this.mediaIdForSlide(slide);
    if (!selectedMediaId) return;
    this.controls().forEach((control) => {
      control.setAttribute('aria-selected', String(!control.hidden && control.dataset.mediaId === selectedMediaId));
    });
  }

  selectFirstVisible() {
    requestAnimationFrame(() => {
      const visibleSlides = this.slides().filter((slide) => !slide.hidden);
      if (!visibleSlides.length) return;
      this.slideshow.select?.(0, undefined, { animate: false });
      requestAnimationFrame(() => this.syncSelectedControls(visibleSlides[0]));
    });
  }

  apply = () => {
    if (!this.refreshElements()) return;
    this.labelElements();
    const selectedGroup = this.selectedGroup();
    if (!selectedGroup) return;

    const groupChanged = selectedGroup !== this.activeGroup;
    this.activeGroup = selectedGroup;

    this.gallery.querySelectorAll('[data-media-group]').forEach((element) => {
      const groups = element.dataset.mediaGroup.split(',');
      element.hidden = !groups.includes('shared') && !groups.includes(normalize(selectedGroup));
    });

    const selectedSlide = this.slides().find((slide) => !slide.hidden && slide.getAttribute('aria-hidden') === 'false');
    if (groupChanged || !selectedSlide) this.selectFirstVisible();
    else this.syncSelectedControls(selectedSlide);
  };

  handleControlClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest(CONTROL_SELECTOR);
    if (!control || control.hidden || !this.gallery.contains(control)) return;

    const visibleSlides = this.slides().filter((slide) => !slide.hidden);
    const targetIndex = visibleSlides.findIndex((slide) => slide.dataset.mediaId === control.dataset.mediaId);
    if (targetIndex < 0) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.slideshow.select?.(targetIndex, event);
  };

  handleSlideSelect = (event) => {
    const slide = event.detail?.slide;
    if (slide instanceof Element) requestAnimationFrame(() => this.syncSelectedControls(slide));
  };

  handleVariantChange = (event) => {
    if (!event.target.closest?.('variant-picker')) return;
    requestAnimationFrame(this.apply);
  };

  handleVariantUpdate = () => {
    requestAnimationFrame(() => requestAnimationFrame(this.apply));
  };
}

const initialize = () => {
  document.querySelectorAll(GROUPING_SELECTOR).forEach((marker) => {
    if (marker.dataset.mediaGroupingReady) return;
    marker.dataset.mediaGroupingReady = 'true';
    new ProductMediaGrouping(marker).connect().catch((error) => console.error(error));
  });
};

initialize();
document.addEventListener('shopify:section:load', initialize);
